// Drawola Studio — confirmation dialog. Fork-only.
//
// Same shape / class conventions as `storage/ui/BlockingQuotaModal.tsx` (a
// plain fixed overlay + island panel) rather than upstream's `<Dialog>`:
// upstream dialogs render through the editor's jotai isolation and a portal
// owned by `<Excalidraw>`, and the Studio lives outside that tree.

import React, { useCallback, useEffect, useId, useRef } from "react";

type Props = {
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  /** Style the confirm button as destructive. */
  danger?: boolean;
  /** Optional secondary choice under the body, like "also delete the cloud copy". */
  checkbox?: {
    label: string;
    checked: boolean;
    disabled?: boolean;
    /** Shown under the label, for example why the box is disabled. */
    hint?: string;
    onChange: (checked: boolean) => void;
  };
  onConfirm: () => void;
  onCancel: () => void;
};

export const StudioConfirmDialog: React.FC<Props> = ({
  title,
  body,
  confirmLabel,
  cancelLabel,
  danger,
  checkbox,
  onConfirm,
  onCancel,
}) => {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const hintId = useId();

  useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // keep editor shortcuts from seeing dialog keystrokes
      event.stopPropagation();
      if (event.key === "Escape") {
        onCancel();
      }
    },
    [onCancel],
  );

  return (
    <div
      className="drawola-studio-dialog"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onKeyDown={onKeyDown}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onCancel();
        }
      }}
    >
      <div className="drawola-studio-dialog__panel">
        <h3 className="drawola-studio-dialog__title">{title}</h3>
        <p className="drawola-studio-dialog__body">{body}</p>
        {checkbox && (
          // The hint sits outside the `<label>` on purpose: inside it, it
          // would become part of the checkbox's accessible name.
          <div className="drawola-studio-dialog__checkbox">
            <label className="drawola-studio-dialog__checkbox-label">
              <input
                type="checkbox"
                checked={checkbox.checked}
                disabled={checkbox.disabled}
                aria-describedby={checkbox.hint ? hintId : undefined}
                onChange={(event) =>
                  checkbox.onChange(event.currentTarget.checked)
                }
              />
              <span>{checkbox.label}</span>
            </label>
            {checkbox.hint && (
              <span id={hintId} className="drawola-studio-dialog__hint">
                {checkbox.hint}
              </span>
            )}
          </div>
        )}
        <div className="drawola-studio-dialog__actions">
          <button
            type="button"
            className="drawola-studio-dialog__button"
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={`drawola-studio-dialog__button drawola-studio-dialog__button--${
              danger ? "danger" : "primary"
            }`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
